import React, { useEffect, useState } from "react";
import classNames from "classnames";
import Payment from "../../../../Components/Payment/Payment";
import PaymentSummary from "./PaymentSummary";
import PackagePaymentTermsConsent from "./PackagePaymentTermsConsent";
import PackageBankTransferInstructions from "./PackageBankTransferInstructions";
import {
  PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER,
  PACKAGE_PROFILE_PAYMENT_CREDIT,
  shouldInstantOpenCreditPay,
} from "../../../../utils/packageProfileCustomerPayment";
import "./PackagePayment.scss";

const PackagePayment = ({
  our_order_id,
  leftToPay,
  sumpaid,
  disabled,
  isCustomerLink = false,
  enablePaymentMethodSegmented = false,
  omitPaymentSummary = false,
  isConditionsConfirmed,
  setIsConditionsConfirmed,
  selectedResult,
  selectedPaymentMethod,
  onPaymentMethodChange,
  lockPaymentMethodToBank = false,
  focusPayLayout = false,
  beforeCreatePaymentLink = null,
  allowInstantOpenPay = true,
  ...props
}) => {
  const enforcePaymentTerms = typeof setIsConditionsConfirmed === "function";
  const [termsAttemptHint, setTermsAttemptHint] = useState(false);
  const focusPayAmountLabel = (() => {
    if (!focusPayLayout) return null;
    const n = Number(leftToPay);
    if (!Number.isFinite(n) || n <= 0) return null;
    return `₪${Math.round(n).toLocaleString("he-IL")}`;
  })();

  const customerMethodUx =
    (isCustomerLink || enablePaymentMethodSegmented) &&
    typeof selectedPaymentMethod === "string" &&
    typeof onPaymentMethodChange === "function";

  useEffect(() => {
    if (isConditionsConfirmed) {
      setTermsAttemptHint(false);
    }
  }, [isConditionsConfirmed]);

  const showCreditForm =
    !customerMethodUx || selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_CREDIT;
  const showBankInstructions =
    customerMethodUx &&
    selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER;

  const renderTermsConsent = (idSuffix) =>
    enforcePaymentTerms ? (
      <PackagePaymentTermsConsent
        checked={!!isConditionsConfirmed}
        onChange={(v) => setIsConditionsConfirmed(v)}
        showAttemptHint={termsAttemptHint}
        checkboxId={
          isCustomerLink
            ? `package-payment-terms-consent-customer-${idSuffix}`
            : `package-payment-terms-consent-agent-${idSuffix}`
        }
      />
    ) : null;

  const termsConsentNode = renderTermsConsent("default");

  const creditPaymentNode = (
    <Payment
      our_order_id={our_order_id}
      leftToPay={leftToPay}
      sumpaid={sumpaid}
      selectedResult={selectedResult}
      paymentTermsAccepted={
        enforcePaymentTerms ? !!isConditionsConfirmed : undefined
      }
      onPaymentTermsBlocked={() => setTermsAttemptHint(true)}
      packageProfileCreditMarkers={customerMethodUx && showCreditForm}
      focusPayLayout={focusPayLayout}
      afterPayLinkSlot={
        customerMethodUx ? renderTermsConsent("credit") : termsConsentNode
      }
      beforeCreatePaymentLink={beforeCreatePaymentLink}
      instantOpenPay={
        showCreditForm &&
        allowInstantOpenPay !== false &&
        shouldInstantOpenCreditPay({
          focusPayLayout,
          isCustomerLink,
        })
      }
    />
  );

  const bankInstructionsNode = (
    <>
      <PackageBankTransferInstructions
        leftToPay={leftToPay}
        omitAmount={focusPayLayout}
      />
      {renderTermsConsent("bank")}
    </>
  );

  return (
    <div className={`package-payment-container ${isCustomerLink ? "customer-payment" : ""}${focusPayLayout ? " package-payment-container--focus" : ""}`}>
      <div className="tw-flex tw-flex-col-reverse lg:tw-flex-row tw-gap-[5%] payment-content-wrapper">
        {!isCustomerLink && !omitPaymentSummary && (
          <div className="lg:tw-w-[35%]">
            <PaymentSummary
              our_order_id={our_order_id}
              disabled={disabled}
              isCustomerLink={isCustomerLink}
              {...props}
            />
          </div>
        )}

        <div className="tw-flex tw-flex-col tw-w-full lg:tw-flex-1 tw-min-w-0 tw-gap-3 package-payment-main-column">
          {focusPayLayout && focusPayAmountLabel && !showCreditForm ? (
            <div className="package-payment-amount-focus" dir="rtl">
              <span className="package-payment-amount-focus__label">לתשלום</span>
              <span className="package-payment-amount-focus__value">{focusPayAmountLabel}</span>
            </div>
          ) : null}
          {customerMethodUx ? (
            <div
              className="package-payment-method-segmented"
              role="tablist"
              aria-label="אמצעי תשלום"
            >
              <button
                type="button"
                role="tab"
                aria-selected={selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_CREDIT}
                disabled={lockPaymentMethodToBank}
                className={classNames("package-payment-method-segmented__btn", {
                  "is-active": selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_CREDIT,
                })}
                onClick={() =>
                  !lockPaymentMethodToBank &&
                  (() => {
                    console.warn("[PENDING_OFFER_PAYMENT_METHOD_CHOSEN]", {
                      method: PACKAGE_PROFILE_PAYMENT_CREDIT,
                    });
                    onPaymentMethodChange(PACKAGE_PROFILE_PAYMENT_CREDIT);
                  })()
                }
              >
                אשראי
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={
                  selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER
                }
                className={classNames("package-payment-method-segmented__btn", {
                  "is-active":
                    selectedPaymentMethod === PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER,
                })}
                onClick={() => {
                  console.warn("[PENDING_OFFER_PAYMENT_METHOD_CHOSEN]", {
                    method: PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER,
                  });
                  onPaymentMethodChange(PACKAGE_PROFILE_PAYMENT_BANK_TRANSFER);
                }}
              >
                העברה בנקאית
              </button>
            </div>
          ) : null}

          {isCustomerLink ? (
            <p className="package-payment-charge-disclaimer" dir="rtl">
              החיוב יתבצע בש&quot;ח לפי שער הדולר במועד התשלום
            </p>
          ) : null}

          {customerMethodUx ? (
            <div className="package-payment-method-stage">
              <div
                className={classNames("package-payment-method-stage__pane", {
                  "is-active": showCreditForm,
                })}
                role="tabpanel"
                aria-hidden={!showCreditForm}
                {...(showCreditForm ? {} : { inert: "" })}
              >
                {creditPaymentNode}
              </div>
              <div
                className={classNames("package-payment-method-stage__pane", {
                  "is-active": showBankInstructions,
                })}
                role="tabpanel"
                aria-hidden={!showBankInstructions}
                {...(showBankInstructions ? {} : { inert: "" })}
              >
                {bankInstructionsNode}
              </div>
            </div>
          ) : showCreditForm ? (
            creditPaymentNode
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PackagePayment;
